import React, { useEffect, useState } from "react";
import {
  Filter,
  Download,
  // Users,
  // Search,
  ChevronDown,
  BarChart3,
  RefreshCw,
  FileText,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  exportLiveSessionsReportCSV,
  exportLiveSessionsReportExcel,
  exportLiveSessionsReportPDF,
  getLiveSessionsReport,
} from "../../../api/reportAPI";
import logger from "../../../utils/logger";
import { toISOForDB } from "../../../utils/Date";
import DateInput from "react-calendar-ui-kit";
interface GraphDataPoint {
  name: string;
  count: number;
}

type FilterType = "today" | "week" | "month" | "year" | "custom" | "All";

// const labelMap: Record<string, string> = {
//   today: "Hours",
//   week: "Week Day",
//   month: "Date",
//   year: "Month",
//   custom: "Date Range",
// };

const Streams: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("All");
  const [applyLoading, setApplyLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState("");
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<GraphDataPoint[]>([]);
  const [totalSessions, setTotalSessions] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  //  Fetch Live Sessions Data on filter change
  useEffect(() => {
    if (selectedFilter === "custom") return; //

    fetchLiveSessionsData();
  }, [selectedFilter]);

  const fetchLiveSessionsData = async () => {
    try {
      setLoading(true);
      setError("");

      const params: any = {
        filter: selectedFilter === "All" ? "today" : selectedFilter,
      };

      if (selectedFilter === "custom") {
        params.fromDate = from;
        params.toDate = to;
      }

      const response = await getLiveSessionsReport(params);

      const { graphData, totalSessions } = response.data.data;

      setGraphData(graphData || []);
      setTotalSessions(totalSessions || 0);
    } catch (err: any) {
      logger.error("Error fetching live sessions data:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  //  Filter Handling
  const handleFilterChange = (filter: FilterType) => {
    setSelectedFilter(filter);
    setError("");

    if (filter === "custom") {
      setFrom("");
      setTo("");
      setGraphData([]); // ✅ CLEAR GRAPH
      setTotalSessions(0); // ✅ SHOW 0
      return;
    }

    // Non-custom filters
    setFrom("");
    setTo("");
  };

  const handleDateInputValidation = (
    value: string,
    setter: (val: string) => void
  ) => {
    if (!value) return setter("");

    const isoValue = toISOForDB(value) ?? "";
    setter(isoValue);
  };

  const handleApplyFilters = async () => {
    if (!from || !to) {
      setError("Please select both From and To dates");
      setGraphData([]);
      setTotalSessions(0);
      return;
    }
    try {
      setApplyLoading(true);
      await fetchLiveSessionsData(); // await ensures loader stays until data fetched
    } finally {
      setApplyLoading(false);
    }
  };

  // const handleReset = () => {
  //   setSelectedFilter("All");
  //   setFrom("");
  //   setTo("");
  //   setError("");
  //   fetchLiveSessionsData();
  // };
  const handleReset = async () => {
    try {
      setResetLoading(true);
      setSelectedFilter("today");
      setFrom("");
      setTo("");
      setError("");
    } catch (error) {
      logger.error("err", error);
      logger.error("err", error);
    } finally {
      setResetLoading(false);
    }
  };

  //  Export Handlers
  // const handleExport = async (type: string) => {
  //   setExportLoading(type);
  //   try {
  //     const params = {
  //       // filter: selectedFilter === "All" ? "today" : selectedFilter,
  //       filter: selectedFilter === "All" ? "year" : selectedFilter,

  //       fromDate: from || undefined,
  //       toDate: to || undefined,
  //     };

  //     let exportFunc;
  //     if (type === "csv") exportFunc = exportLiveSessionsReportCSV;
  //     else if (type === "excel") exportFunc = exportLiveSessionsReportExcel;
  //     else exportFunc = exportLiveSessionsReportPDF;

  //     const res = await exportFunc(params);
  //     const blob = new Blob([res.data]);
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = `live-sessions-report.${type}`;
  //     link.click();
  //   } catch (err: any) {
  //     logger.error(err);
  //     setError("Failed to export report. Please try again.");
  //   } finally {
  //     setExportLoading(null);
  //   }
  // };
  const handleExport = async (type: string) => {
    setExportLoading(type);
    try {
      const params = {
        filter: selectedFilter === "All" ? "today" : selectedFilter,

        fromDate: from || undefined,
        toDate: to || undefined,
      };

      let exportFunc;
      if (type === "csv") exportFunc = exportLiveSessionsReportCSV;
      else if (type === "excel") exportFunc = exportLiveSessionsReportExcel;
      else exportFunc = exportLiveSessionsReportPDF;

      const res = await exportFunc(params);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      //  FIXED EXTENSIONS
      const extMap: any = {
        csv: "csv",
        excel: "xlsx",
        pdf: "pdf",
      };

      link.download = `live-sessions-report.${extMap[type]}`;
      link.click();
    } catch (err: any) {
      logger.error(err);
      setError("Failed to export report. Please try again.");
    } finally {
      setExportLoading(null);
    }
  };

  //  Line Chart Renderer
  const renderLineChart = (data: GraphDataPoint[], color: string) => (
    <div className="h-80 w-full mt-5">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 0,
            right: 20,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          {/* <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => value}
            // label={{
            //   value: "Date / Month",
            //   position: "insideBottom",
            //   offset: -5,
            //   style: { fontSize: 13, fill: "#374151" },
            // }}
            label={{
              value:
                selectedFilter === "week"
                  ? "Week Day"
                  : selectedFilter === "today"
                  ? "Hour"
                  : selectedFilter === "month"
                  ? "Date"
                  : selectedFilter === "year"
                  ? "Month"
                  : "Date",
              position: "insideBottom",
              offset: -5,
              style: { fontSize: 13, fill: "#374151" },
            }}
          />

          <YAxis
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            domain={[0, "auto"]}
            label={{
              value: "Total Live Sessions",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 13, fill: "#374151" },
            }}
          /> */}
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            tickMargin={0}
            tickFormatter={(value) => {
              const d = new Date(value);

              // TODAY → hour
              if (selectedFilter === "today") {
                return d.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
              }

              // WEEK → weekday name
              if (selectedFilter === "week") {
                return d.toLocaleDateString("en-US", {
                  weekday: "short", // Mon, Tue
                });
              }

              // MONTH → date
              if (selectedFilter === "month") {
                return d.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short", // 09 Dec
                });
              }

              // YEAR → month name
              if (selectedFilter === "year") {
                return d.toLocaleDateString("en-US", {
                  month: "short", // Jan, Feb
                });
              }

              // fallback
              return value;
            }}
            label={{
              value:
                selectedFilter === "week"
                  ? "Week Day"
                  : selectedFilter === "month"
                    ? "Date"
                    : selectedFilter === "year"
                      ? "Month"
                      : "Time",
              position: "insideBottom",
              offset: -5,
            }}
          />

          <YAxis
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            label={{
              value: "Total Live Sessions",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 13, fill: "#374151" },
            }}
          />
          <Tooltip
            formatter={(value: any) => [value, "Sessions"]}
            labelFormatter={(value: any) => `${value}`}
          />
          <Line
            type="monotone"
            dataKey="count"
            stroke={color}
            strokeWidth={3}
            dot={{ fill: color, r: 4 }}
            activeDot={{ r: 6, stroke: color, strokeWidth: 2, fill: "#fff" }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="min-h-screen  max-w-screen">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        {/* 🔹 Filters Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            </div>

            <div className="space-y-4">
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3">
                {["All", "custom", "week", "month", "year"].map((f) => (
                  <label
                    key={f}
                    className={`px-4 py-2 rounded-lg border cursor-pointer transition-colors
                    ${
                      selectedFilter === f
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700 font-medium"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="filter"
                      value={f}
                      checked={selectedFilter === f}
                      onChange={(e) =>
                        handleFilterChange(e.target.value as FilterType)
                      }
                      className="hidden"
                    />
                    {f === "custom"
                      ? "Date Range"
                      : f === "week"
                        ? "This Week"
                        : f === "month"
                          ? "This Month"
                          : f === "year"
                            ? "This Year"
                            : "Today"}
                  </label>
                ))}
              </div>

              {/* Custom Date Range */}
              {selectedFilter === "custom" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 ">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      From Date
                    </label>
                    <DateInput
                      value={from}
                      onChange={(val: string) => {
                        handleDateInputValidation(val, setFrom);
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      To Date
                    </label>
                    <DateInput
                      value={to}
                      onChange={(val: string) => {
                        handleDateInputValidation(val, setTo);
                      }}
                    />
                  </div>

                  <div className="flex items-end space-x-2">
                    <button
                      disabled={applyLoading}
                      onClick={handleApplyFilters}
                      className="w-48 inline-flex items-center px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                    >
                      {applyLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Filter className="w-4 h-4 mr-2" />
                      )}
                      {applyLoading ? "Applying..." : "Apply Filters"}
                    </button>



                    <button
                      onClick={handleReset}
                      disabled={resetLoading}
                      className={`inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors
                                                                                      ${resetLoading ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${resetLoading ? "animate-spin" : ""}`}
                      />
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/*  Export Dropdown */}
          <div className="flex justify-end mt-4">
            <div className="relative">
              <button
                disabled={!!exportLoading}
                className="inline-flex items-center justify-center w-48 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                onClick={() => {
                  const exportOptions =
                    document.getElementById("export-dropdown");
                  exportOptions?.classList.toggle("hidden");
                }}
              >
                {exportLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportLoading ? "Exporting..." : "Export Report"}
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>

              <div
                id="export-dropdown"
                className="hidden absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20"
              >
                <div className="py-1">
                  {(["csv", "excel", "pdf"] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleExport(type)}
                      disabled={!!exportLoading}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      {exportLoading === type && (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Download as {type.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*  Summary Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 col-span-3">
            <p className="text-sm font-medium text-gray-600">
              Total Live Sessions
            </p>
            <p className="text-3xl font-bold text-blue-600">
              {loading ? "Loading..." : totalSessions}
            </p>
          </div>
        </div>

        {/*  Graph Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
            Live Sessions Overview
          </h3>
          {loading ? (
            <div className="text-center text-gray-500 py-10">
              Loading chart...
            </div>
          ) : graphData.length === 0 ? (
            <div className="flex flex-col items-center justify-center space-y-3">
              <FileText className="h-12 w-12 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-600 mb-0">
                No Data Found
              </h3>
              <p className="text-xs text-gray-400">
                Try adjusting your filters to see more results
              </p>
            </div>
          ) : (
            renderLineChart(graphData, "#3b82f6")
          )}
        </div>
      </div>
    </div>
  );
};

export default Streams;
